import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useFeatureFlags } from 'feature-flags';
import { Navigate, useLocation, useParams } from 'src/lib/routing';
import { useAIQueryAccess } from 'src/hooks/useAIQueryAccess';
import { useAIRepoAccess } from 'src/hooks/useAIRepoAccess';
import { useProductAccess } from 'src/hooks/useProductAccess';

import type { FC, ReactNode } from 'react';

export const BookingsRedirect = () => {
  const params = useParams();
  const pathname = `/bookings/${params.bookingID}${params.screen && params.screen !== 'applicants' ? `/${params.screen}` : ''}`;
  return <Navigate replace to={{ pathname }} />;
};

export const RouteProtection: FC<{ children: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const { isRouteAllowed } = useProductAccess();

  if (pathname === '/settings') {
    return isRouteAllowed('/settings/account') ? (
      <Navigate to="/settings/account" replace />
    ) : (
      <Navigate to="/not-found" replace />
    );
  }

  return isRouteAllowed(pathname) || pathname === '/' ? <>{children}</> : <Navigate to="/not-found" replace />;
};

// Index redirect to the user's default route (product-access dependent).
export const DefaultRedirect = () => {
  const { defaultRoute } = useProductAccess();
  return <Navigate to={defaultRoute} replace />;
};

// The AI-query routes (`chat`, `docs`, `highlights`) used to be conditionally
// included on `queryAccess`. With a static route tree they always exist and
// this guard sends users without access to `/not-found` instead.
export const RequireQueryAccess: FC<{ children: ReactNode }> = ({ children }) => {
  const queryAccess = useAIQueryAccess();
  return queryAccess ? <>{children}</> : <Navigate to="/not-found" replace />;
};

// The `streams/*` subtree used to be conditionally mounted. This guard keeps
// the same access boundary (anonymous trial visitors, or Industry Streams teams
// with the UI flag on) — without it, non-streams teams could URL-jump in.
export const RequireStreams: FC<{ children: ReactNode }> = ({ children }) => {
  const kinde = useKindeAuth();
  const { activeProduct } = useProductAccess();
  const { industry_streams_ui } = useFeatureFlags(['industry_streams_ui']);
  const isStreamsMounted = !kinde.isAuthenticated || (industry_streams_ui && activeProduct === 'industry_stream');
  return isStreamsMounted ? <>{children}</> : <Navigate to="/not-found" replace />;
};

// The `explorer` subtree used to be conditionally mounted on
// `ai_evidence_explorer && hasAIRepoAccess`. The route tree is static now, so
// this guard keeps the same access boundary and falls through to `/not-found`.
export const RequireExplorer: FC<{ children: ReactNode }> = ({ children }) => {
  const hasAIRepoAccess = useAIRepoAccess();
  const { ai_evidence_explorer } = useFeatureFlags(['ai_evidence_explorer']);
  return ai_evidence_explorer && hasAIRepoAccess ? <>{children}</> : <Navigate to="/not-found" replace />;
};

export const RequireModeratedScheduling: FC<{ children: ReactNode }> = ({ children }) => {
  const { moderated_scheduling_enabled } = useFeatureFlags(['moderated_scheduling_enabled']);
  return moderated_scheduling_enabled ? <>{children}</> : <Navigate to="/not-found" replace />;
};
